import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetail from "@/shared/components/transactions/TransactionDetail";
import { Button } from "@/shared/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function Transactions() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    status: "all",
    merchant: "all",
    provider: "all",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const queryClient = useQueryClient();

  const { data: qrCodes = [], isLoading } = useQuery({
    queryKey: ["qr-codes"],
    queryFn: () => qrCodeService.getAllQRCodes(),
    refetchInterval: 5000, // Refetch every 5 seconds to update status values
    refetchOnWindowFocus: true, // Refetch when window regains focus
    staleTime: 0, // Consider data stale immediately to allow refetching
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  // Filter qr-codes
  const filteredQRCodes = React.useMemo(() => {
    return qrCodes.filter((qr) => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (
          !qr.payment_id?.toLowerCase().includes(search) &&
          !qr.reference?.toLowerCase().includes(search) &&
          !qr.merchant?.name?.toLowerCase().includes(search) &&
          !qr.merchant?.code?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }
      if (filters.status !== "all" && qr.status !== filters.status) return false;
      if (filters.merchant !== "all" && qr.merchant_id !== filters.merchant) return false;
      if (filters.provider !== "all" && qr.client_bank_name !== filters.provider) return false;
      return true;
    });
  }, [qrCodes, filters]);

  // Pagination
  const paginatedQRCodes = filteredQRCodes.slice(
    page * pageSize,
    (page + 1) * pageSize
  );
  const totalPages = Math.ceil(filteredQRCodes.length / pageSize);

  const handleReset = () => {
    setFilters({
      search: "",
      type: "all",
      status: "all",
      merchant: "all",
      provider: "all",
    });
    setPage(0);
  };

  const handleExport = () => {
    // Export filtered qr-codes as CSV
    const headers = [t("paymentId"), t("reference"), t("merchant"), t("reseller"), t("provider"), t("amount"), t("status"), t("date")];
    const rows = filteredQRCodes.map((qr) => [
      qr.payment_id,
      qr.reference || "",
      qr.merchant?.name || qr.merchant?.code || "",
      qr.merchant?.createdByResellerId ? (qr.reseller?.name || "") : "Direct",
      qr.client_bank_name || "",
      (qr.amount).toString(),
      qr.status,
      qr.createdAt,
    ]);
    
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-codes-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t("transactions")}</h1>
        <p className="text-slate-500">
          {filteredQRCodes.length} {t("transactionsFound")}
        </p>
      </div>

      {/* Filters */}
      <TransactionFilters
        filters={filters}
        onFilterChange={(f) => {
          setFilters(f);
          setPage(0);
        }}
        merchants={merchants}
        providers={providers}
        onExport={handleExport}
        onReset={handleReset}
      />

      {/* Table */}
      <TransactionTable
        transactions={paginatedQRCodes}
        isLoading={isLoading}
        onView={setSelectedTransaction}
        merchants={merchants}
        resellers={resellers}
        isQRCode={true}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t("showing")} {page * pageSize + 1} {t("to")}{" "}
            {Math.min((page + 1) * pageSize, filteredQRCodes.length)} {t("of")}{" "}
            {filteredQRCodes.length} {t("transactions")}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600">
              {t("page")} {page + 1} {t("of")} {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Sheet */}
      <TransactionDetail
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        isQRCode={true}
      />
    </div>
  );
}